import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState, useRef, useCallback } from 'react';
import UpdateValidatorInfoRequest from '../../models/UpdateValidateInfoRequest';
import {
  setIpPortNodeAddress,
  checkValidatorEpochStatus,
  getMinimumSelfStake,
  getSelfStake,
} from '../../services/staking';
import { useRecoilValue } from 'recoil';
import { contractAddressesState } from '../../context/appContext';
import { ethers } from 'ethers';
import { chainNames } from '../setContractAddresses/utils';
import { getSigner } from '../../lib/web3';

export default function UpdateValidatorInfoForm() {
  const stakingContractAddress = useRecoilValue(contractAddressesState);

  const handleUpdateValidatorInfo = async (
    request: UpdateValidatorInfoRequest
  ) => {
    try {
      await setIpPortNodeAddress(
        request,
        stakingContractAddress.stakingAddress
      );
    } catch (e: any) {
      console.log('Error updating validator info: ', e);
      alert('Error updating validator info: ' + e.message);
    }
  };

  const [ip, setIp] = useState<string>('');
  const [ipv6, setIpv6] = useState<string>('0');
  const [port, setPort] = useState<string>('');
  const [nodeAddress, setNodeAddress] = useState<string>('');
  const [stakerAddress, setStakerAddress] = useState<string>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [tokenBalance, setTokenBalance] = useState<string | null>(null);
  const [tokenSymbol, setTokenSymbol] = useState<string | null>(null);
  const [inCurrentEpoch, setInCurrentEpoch] = useState<boolean>(false);
  const [inNextEpoch, setInNextEpoch] = useState<boolean>(false);
  const [minSelfStake, setMinSelfStake] = useState<ethers.BigNumber | null>(
    null
  );
  const [selfStake, setSelfStake] = useState<ethers.BigNumber | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const signer = await getSigner();
        const addr = await signer.getAddress();
        setStakerAddress(addr);
      } catch (e) {
        // ignore if wallet not connected
        setStakerAddress('');
      }
    })();
  }, []);

  const updateEpochStatusAndSelfStake = useCallback(async () => {
    if (!stakingContractAddress?.stakingAddress || !stakerAddress) {
      return;
    }

    try {
      // Check epoch status
      const epochStatus = await checkValidatorEpochStatus(
        stakingContractAddress.stakingAddress,
        stakerAddress
      );
      setInCurrentEpoch(epochStatus.inCurrentEpoch);
      setInNextEpoch(epochStatus.inNextEpoch);

      // Get minimum self stake
      const minStake = await getMinimumSelfStake(
        stakingContractAddress.stakingAddress
      );
      setMinSelfStake(minStake);

      // Get current self stake
      const currentSelfStake = await getSelfStake(
        stakingContractAddress.stakingAddress,
        stakerAddress
      );
      setSelfStake(currentSelfStake);
    } catch (e: any) {
      console.error('Error updating epoch status and self-stake:', e);
      // Don't update state on error to keep previous values
    }
  }, [stakingContractAddress?.stakingAddress, stakerAddress]);

  useEffect(() => {
    // Initial update
    updateEpochStatusAndSelfStake();

    // Set up interval to update every 10 seconds
    intervalRef.current = setInterval(() => {
      updateEpochStatusAndSelfStake();
    }, 10000);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [updateEpochStatusAndSelfStake]);

  const fetchTokenBalance = async (address: string) => {
    try {
      if (!ethers.utils.isAddress(address)) {
        setTokenBalance(null);
        setTokenSymbol(null);
        return;
      }
      const provider = new ethers.providers.JsonRpcProvider(stakingContractAddress.chain.rpcUrl);
      const rawBalance = await provider.getBalance(address);
      const formatted = ethers.utils.formatUnits(rawBalance, 18);
      const symbol =
        stakingContractAddress.chain.name === chainNames.LIT_CHAIN ? 'LITKEY' : 'tstLPX';
      setTokenBalance(formatted);
      setTokenSymbol(symbol);
    } catch (err) {
      console.error('Failed to fetch token balance', err);
      setTokenBalance(null);
      setTokenSymbol(null);
    }
  };

  // Check if inputs should be disabled
  const isDisabled =
    inCurrentEpoch ||
    inNextEpoch ||
    (selfStake !== null &&
      minSelfStake !== null &&
      selfStake.lt(minSelfStake));

  // Check if self-stake is insufficient
  const hasInsufficientSelfStake =
    selfStake !== null &&
    minSelfStake !== null &&
    selfStake.lt(minSelfStake);

  // Format self-stake and min self-stake for display
  const formatStake = (stake: ethers.BigNumber | null): string => {
    if (!stake) return '...';
    return ethers.utils.formatUnits(stake, 18);
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2} sx={{ m: 2 }}>
          <Typography variant={'h5'}>Set Validator Info</Typography>

          {/* Warning messages */}
          {(inCurrentEpoch || inNextEpoch) && (
            <Alert severity="warning">
              {inCurrentEpoch && inNextEpoch
                ? 'Validator is active in both current and next epoch. Inputs are disabled.'
                : inCurrentEpoch
                ? 'Validator is active in current epoch. Inputs are disabled.'
                : 'Validator is active in next epoch. Inputs are disabled.'}
            </Alert>
          )}

          {hasInsufficientSelfStake && (
            <Alert severity="warning">
              Insufficient self-stake. Current: {formatStake(selfStake)}{' '}
              {tokenSymbol || 'tokens'}, Required: {formatStake(minSelfStake)}{' '}
              {tokenSymbol || 'tokens'}. Please stake at least the minimum
              self-stake amount.
            </Alert>
          )}

          <TextField
            fullWidth
            size={'small'}
            value={ip}
            onChange={(e) => setIp(e.target.value)}
            label={'IP'}
            disabled={isDisabled}
          />
          <TextField
            fullWidth
            size={'small'}
            value={ipv6}
            onChange={(e) => setIpv6(e.target.value)}
            label={'IPv6 (not implemented)'}
            disabled
          />
          <TextField
            fullWidth
            size={'small'}
            value={port}
            onChange={(e) => setPort(e.target.value)}
            label={'Port'}
            disabled={isDisabled}
          />
          <TextField
            fullWidth
            size={'small'}
            value={nodeAddress}
            onChange={async (e) => {
              const val = e.target.value;
              setNodeAddress(val);
              await fetchTokenBalance(val);
            }}
            label={
              tokenSymbol !== null
                ? `Node Address — Balance: ${tokenBalance} ${tokenSymbol}`
                : 'Node Address'
            }
            disabled={isDisabled}
          />
          <Button
            fullWidth
            variant={'contained'}
            disabled={isDisabled}
            onClick={async () => {
              // Ensure node address has a non-zero native balance before proceeding
              if (ethers.utils.isAddress(nodeAddress)) {
                try {
                  const provider = new ethers.providers.JsonRpcProvider(
                    stakingContractAddress.chain.rpcUrl
                  );
                  const raw = await provider.getBalance(nodeAddress);
                  if (raw.isZero()) {
                    const symbol =
                      stakingContractAddress.chain.name === chainNames.LIT_CHAIN
                        ? 'LITKEY'
                        : 'tstLPX';
                    setValidationError(
                      `Node address has 0 ${symbol}. Please fund this address and try again.`
                    );
                    return;
                  }
                } catch (e: any) {
                  setValidationError('Failed to check node address balance.');
                  return;
                }
              }
              // Construct request
              const request = new UpdateValidatorInfoRequest({
                ip: ip,
                ipv6: ipv6,
                port: port,
                nodeAddress: nodeAddress,
              });

              // Validate request
              try {
                await request.validate();
                setValidationError(null);
              } catch (error: any) {
                setValidationError(error.message);
                return;
              }

              // Send request
              await handleUpdateValidatorInfo(request);
            }}
          >
            Set Validator Info
          </Button>
          {validationError && (
            <Typography color={'error'}>{validationError}</Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export interface UpdateValidatorInfoFormProps {
  onSubmit: (request: UpdateValidatorInfoRequest) => void;
}
